import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ShoppingCart, Plus, GitMerge } from 'lucide-react';

const STADE_LABELS = {
  devis:        'Devis',
  devis_envoye: 'Devis envoyé',
};

/**
 * Modale d'agrégation des Commandes d'Achat.
 *
 * Props:
 *   candidats  : { [fournisseur_nom]: [{ achat_id, numero, stade, nb_lignes }] }
 *   onConfirm  : (choix: { [fournisseur_nom]: achat_id | "new" }) => void
 *   onClose    : () => void
 */
export function AggregationCAModal({ candidats, onConfirm, onClose }) {
  const fournisseurs = Object.keys(candidats || {});

  const [choix, setChoix] = useState(() => {
    const init = {};
    fournisseurs.forEach(f => {
      init[f] = candidats[f]?.[0]?.achat_id || 'new';
    });
    return init;
  });

  if (!fournisseurs.length) return null;

  return (
    <Dialog.Root open onOpenChange={open => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(15,23,42,0.55)',
            backdropFilter: 'blur(4px)',
          }}
        />
        <Dialog.Content
          onInteractOutside={onClose}
          onEscapeKeyDown={onClose}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            width: '100%',
            maxWidth: 520,
            margin: '0 16px',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            border: '1px solid #E2E8F0',
            outline: 'none',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EFF6FF' }}>
                <GitMerge size={16} style={{ color: '#1D4ED8' }} />
              </div>
              <div>
                <Dialog.Title style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A' }}>
                  Agrégation des commandes d'achat
                </Dialog.Title>
                <Dialog.Description style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
                  Des CA en cours existent pour ces fournisseurs — que faire ?
                </Dialog.Description>
              </div>
            </div>
            <button
              data-testid="aggregation-modal-close"
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4, display: 'flex', alignItems: 'center' }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 24px', overflowY: 'auto', maxHeight: 420 }}>
            {fournisseurs.map(fourn => (
              <div key={fourn} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <ShoppingCart size={12} style={{ color: '#6B7280' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#374151' }}>
                    {fourn}
                  </span>
                </div>

                <div style={{ paddingLeft: 4 }}>
                  {/* CA existantes */}
                  {candidats[fourn].map(ca => (
                    <label
                      key={ca.achat_id}
                      data-testid={`aggr-ca-option-${ca.achat_id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        borderRadius: 12,
                        cursor: 'pointer',
                        border: `1px solid ${choix[fourn] === ca.achat_id ? '#BFDBFE' : '#E5E7EB'}`,
                        background: choix[fourn] === ca.achat_id ? '#EFF6FF' : '#FAFAFA',
                        marginBottom: 8,
                      }}
                    >
                      <input
                        type="radio"
                        name={`fourn-${fourn}`}
                        value={ca.achat_id}
                        checked={choix[fourn] === ca.achat_id}
                        onChange={() => setChoix(c => ({ ...c, [fourn]: ca.achat_id }))}
                        style={{ accentColor: '#2563EB' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1D4ED8' }}>{ca.numero}</div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          {STADE_LABELS[ca.stade] || ca.stade}
                          {' · '}
                          {ca.nb_lignes} ligne{ca.nb_lignes > 1 ? 's' : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 9999, fontWeight: 600, background: '#DCFCE7', color: '#15803D', flexShrink: 0 }}>
                        Ajouter à cette CA
                      </span>
                    </label>
                  ))}

                  {/* Option : nouvelle CA */}
                  <label
                    data-testid={`aggr-ca-option-new-${fourn}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 12,
                      borderRadius: 12,
                      cursor: 'pointer',
                      border: `1px solid ${choix[fourn] === 'new' ? '#BBF7D0' : '#E5E7EB'}`,
                      background: choix[fourn] === 'new' ? '#F0FDF4' : '#FAFAFA',
                      marginBottom: 0,
                    }}
                  >
                    <input
                      type="radio"
                      name={`fourn-${fourn}`}
                      value="new"
                      checked={choix[fourn] === 'new'}
                      onChange={() => setChoix(c => ({ ...c, [fourn]: 'new' }))}
                      style={{ accentColor: '#16A34A' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: '#D1FAE5' }}>
                        <Plus size={12} style={{ color: '#15803D' }} />
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#15803D' }}>Créer une nouvelle CA</span>
                    </div>
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 24px', display: 'flex', gap: 12, justifyContent: 'flex-end', borderTop: '1px solid #E2E8F0' }}>
            <button
              onClick={onClose}
              style={{ padding: '8px 16px', borderRadius: 12, fontSize: 14, fontWeight: 600, border: '1px solid #E2E8F0', color: '#64748B', background: '#fff', cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              data-testid="aggregation-confirmer-btn"
              onClick={() => onConfirm(choix)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#fff', background: '#1D4ED8', border: 'none', cursor: 'pointer' }}
            >
              <ShoppingCart size={14} />
              Confirmer la commande
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
